// 82 §4 — THE BROWSER HALF OF THE CEREMONY, WHICH IS MARSHALLING AND NOTHING ELSE.
//
// This file does NOT implement WebAuthn. 82 §3 rules that the ceremony is not
// hand-rolled, and it is not: every byte that matters is produced by the
// authenticator and verified by `@passwordless-id/webauthn` on the server
// (`server/src/lib/webauthnCeremony.js`). What is left over on this side is an
// encoding problem — `navigator.credentials` speaks ArrayBuffer and JSON does
// not — and that is all this file is.
//
// WHY NOT THE LIBRARY'S OWN BROWSER HALF: it ships one, and using it would mean
// a SECOND copy of the package in the frontend's dependency tree (the server's
// is in server/package.json). 82 §10 says not to widen what the ceremony needs,
// and what the ceremony needs here is base64url in two directions.

/** base64url → ArrayBuffer. Padding is optional on the way in; browsers omit it. */
function fromB64u(s: string): ArrayBuffer {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/");
  const pad = b64.length % 4 ? "=".repeat(4 - (b64.length % 4)) : "";
  const bin = atob(b64 + pad);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

/** ArrayBuffer → base64url, UNPADDED — which is what the spec puts in clientDataJSON. */
function toB64u(buf: ArrayBuffer | null): string {
  if (!buf) return "";
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Whether this browser can do the ceremony at all. */
export const passkeysAvailable = (): boolean =>
  typeof window !== "undefined" &&
  !!window.PublicKeyCredential &&
  !!navigator.credentials;

/**
 * ── WHAT CAN BE ASKED IN ADVANCE, AND WHAT CANNOT ───────────────────────────
 *
 * `isUserVerifyingPlatformAuthenticatorAvailable()` answers ONE question
 * honestly: does this machine have a built-in authenticator — Touch ID, Windows
 * Hello, an Android screen lock. It does NOT answer "can this person use a
 * passkey", because a security key on a USB port and a phone reached over
 * hybrid (the QR-code path) both work without one.
 *
 * So the three states are exactly what is knowable, and no more:
 *
 *   'unsupported'  — no WebAuthn API at all. Nothing can work, cross-device
 *                    included, and that IS knowable in advance.
 *   'no-platform'  — the API is here and this machine has no built-in
 *                    authenticator. A security key or a phone may still work,
 *                    so the control stays live and the screen says what is
 *                    needed instead of refusing.
 *   'ok'           — a built-in authenticator is present.
 *
 * There is deliberately no fourth state for "the person has no passkey on this
 * device": nothing can tell us that before the ceremony, and guessing at it is
 * what `classifyFailure` refuses to do afterwards.
 */
export type PasskeyCapability = "ok" | "no-platform" | "unsupported";

export async function passkeyCapability(): Promise<PasskeyCapability> {
  if (!passkeysAvailable()) return "unsupported";
  try {
    const fn = window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable;
    if (typeof fn !== "function") return "no-platform";
    return (await fn.call(window.PublicKeyCredential)) ? "ok" : "no-platform";
  } catch {
    // The probe itself failing tells us nothing about the ceremony, so it must
    // not become a refusal: fall back to the state that keeps the control live.
    return "no-platform";
  }
}

/**
 * ── THE FAILURE CLASSES, AND THE ONE THAT IS DELIBERATELY BROAD ─────────────
 *
 * Every value here maps to a WRITTEN SENTENCE in en/fr/nl at the call site. No
 * `DOMException`, no error name and no browser string ever reaches a screen —
 * a raw code path surfacing as visible debris is the class the register calls
 * 82i, and this is its third instance in a week.
 *
 * `not_completed` is broad ON PURPOSE. `NotAllowedError` is what the browser
 * raises for a cancelled prompt AND for "nothing here could answer" — the
 * platform refuses to distinguish them, deliberately, because telling a website
 * which authenticators a machine holds is a fingerprinting channel. **So we do
 * not try.** One class, one message, and the message says WHAT TO DO NEXT
 * rather than guessing what went wrong.
 */
export type PasskeyFailure =
  | "timeout"            // OUR timer fired — the only one we can state as fact
  | "not_completed"      // cancelled, or nothing could answer. Indistinguishable.
  | "already_enrolled"   // this authenticator already holds a credential for us
  | "wrong_domain"       // the RP id does not match the origin — 82 §8's seam
  | "unsupported_device" // the authenticator cannot meet what was asked
  | "browser_too_old"    // our own check: no getPublicKey/getAuthenticatorData
  | "failed";            // anything else, still a written sentence

/**
 * EVERY KEY THE FAILURE MAPPER CAN EMIT, WRITTEN OUT IN FULL.
 *
 * The modal composes these from a class and a screen, and a composed key is a
 * key no instrument can find — brief 36's finding, and the reason a count of
 * unresolvable call sites never shrinks. So the enumeration lives here as plain
 * literals: `scripts/lint/passkey-failure-texts.js` greps this array and proves
 * every one of them has an en, fr and nl row.
 *
 * A new `PasskeyFailure` value that is not added here fails that lint, which is
 * the point — requirement 1 is "EVERY failure is a written sentence", and a
 * promise about every member of a set needs the set to be enumerable.
 */
export const PASSKEY_MESSAGE_KEYS: readonly string[] = [
  "auth.passkey.unsupported_browser",
  "auth.passkey.no_platform",
  "auth.passkey.use_not_completed_code",
  "auth.passkey.use_not_completed_recovery",
  "auth.passkey.use_timeout_code",
  "auth.passkey.use_timeout_recovery",
  "auth.passkey.use_already_enrolled",
  "auth.passkey.use_wrong_domain",
  "auth.passkey.use_unsupported_device",
  "auth.passkey.use_browser_too_old",
  "auth.passkey.use_failed",
  "auth.passkey.enrol_not_completed",
  "auth.passkey.enrol_timeout",
  "auth.passkey.enrol_already_enrolled",
  "auth.passkey.enrol_wrong_domain",
  "auth.passkey.enrol_unsupported_device",
  "auth.passkey.enrol_browser_too_old",
  "auth.passkey.enrol_failed",
];

/** Thrown by the two ceremonies below when OUR timer fires, never by a browser. */
class PasskeyTimeout extends Error {
  readonly isPasskeyTimeout = true;
  constructor() { super("passkey ceremony timed out"); }
}

export function classifyFailure(e: unknown): PasskeyFailure {
  if (e && (e as PasskeyTimeout).isPasskeyTimeout) return "timeout";
  const name = (e as { name?: string })?.name || "";
  const msg = (e as { message?: string })?.message || "";
  if (msg === "this browser is too old to enrol a passkey") return "browser_too_old";
  switch (name) {
    // AbortError reaches here only when something OTHER than our timer aborted
    // the ceremony; ours is caught above. Both belong in the broad class.
    case "NotAllowedError":
    case "AbortError":
      return "not_completed";
    case "InvalidStateError":
      return "already_enrolled";
    case "SecurityError":
      return "wrong_domain";
    case "NotSupportedError":
    case "ConstraintError":
      return "unsupported_device";
    default:
      return "failed";
  }
}

/**
 * The ceremony timeout, ours rather than the browser's.
 *
 * `PublicKeyCredentialCreationOptions.timeout` is a HINT the platform may
 * ignore, and when it does fire it surfaces as `NotAllowedError` —
 * indistinguishable from a cancel, so a timeout could never be stated as a
 * fact. An `AbortController` we own can be: when this timer fires we KNOW what
 * happened, and the screen can say so plainly instead of guessing.
 */
const CEREMONY_TIMEOUT_MS = 90_000;

function withTimeout<T>(run: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => { timedOut = true; controller.abort(); }, CEREMONY_TIMEOUT_MS);
  return run(controller.signal)
    .catch((e) => { throw timedOut ? new PasskeyTimeout() : e; })
    .finally(() => clearTimeout(timer));
}

/** What the server's `creationOptions` sends, before decoding. */
export interface CreationOptionsJSON {
  challenge: string;
  rp: { id: string; name: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: { type: "public-key"; alg: number }[];
  authenticatorSelection?: Record<string, unknown>;
  attestation?: string;
  excludeCredentials?: { type: "public-key"; id: string; transports?: string[] }[];
  timeout?: number;
}

/** What the server's `requestOptions` sends, before decoding. */
export interface RequestOptionsJSON {
  challenge: string;
  rpId: string;
  userVerification?: string;
  allowCredentials?: { type: "public-key"; id: string; transports?: string[] }[];
  timeout?: number;
}

/**
 * Run the registration ceremony. Returns the object
 * `webauthnCeremony.verifyRegistration` expects, or throws.
 */
export async function createPasskey(options: CreationOptionsJSON) {
  const publicKey: PublicKeyCredentialCreationOptions = {
    ...options,
    challenge: fromB64u(options.challenge),
    user: { ...options.user, id: fromB64u(options.user.id) },
    excludeCredentials: (options.excludeCredentials || []).map((c) => ({
      ...c,
      id: fromB64u(c.id),
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
  } as PublicKeyCredentialCreationOptions;

  const cred = (await withTimeout((signal) =>
    navigator.credentials.create({ publicKey, signal }))) as PublicKeyCredential | null;
  if (!cred) throw new Error("no credential");
  const res = cred.response as AuthenticatorAttestationResponse;

  // `getPublicKey()` and `getAuthenticatorData()` are what let the server verify
  // without parsing CBOR — the reason the dependency has no transitive surface
  // (docs/dependency-audit-2026-08-15-webauthn.md §2). A browser too old for
  // them cannot enrol, and says so here rather than failing opaquely on the
  // server.
  if (typeof res.getPublicKey !== "function" || typeof res.getAuthenticatorData !== "function") {
    throw new Error("this browser is too old to enrol a passkey");
  }

  // 83 (glance) — WHAT THE AUTHENTICATOR ACTUALLY DID. `residentKey: 'required'`
  // is a REQUEST; `credProps.rk` is the answer, and the server stores it so that
  // the per-class exclusion (`webauthnCeremony.excludeFor`) reasons about the
  // credential that exists rather than the one that was asked for. Undefined on
  // browsers that do not implement the extension, and the server falls back to
  // the intent there — which it says in its own comment rather than pretending
  // it was told.
  let credProps: { rk?: boolean } | undefined;
  try {
    const ext = cred.getClientExtensionResults?.() as { credProps?: { rk?: boolean } } | undefined;
    if (ext && ext.credProps && typeof ext.credProps.rk === "boolean") {
      credProps = { rk: ext.credProps.rk };
    }
  } catch {
    // An extension the browser does not implement is not a failure to enrol.
    credProps = undefined;
  }

  return {
    id: cred.id,
    type: cred.type,
    ...(credProps ? { credProps } : {}),
    response: {
      clientDataJSON: toB64u(res.clientDataJSON),
      authenticatorData: toB64u(res.getAuthenticatorData()),
      publicKey: toB64u(res.getPublicKey()),
      publicKeyAlgorithm: res.getPublicKeyAlgorithm(),
      transports: typeof res.getTransports === "function" ? res.getTransports() : [],
    },
  };
}

/**
 * Run the authentication ceremony. Returns the object
 * `webauthnCeremony.verifyAuthentication` expects, or throws.
 */
export async function assertPasskey(options: RequestOptionsJSON) {
  const publicKey: PublicKeyCredentialRequestOptions = {
    ...options,
    challenge: fromB64u(options.challenge),
    allowCredentials: (options.allowCredentials || []).map((c) => ({
      ...c,
      id: fromB64u(c.id),
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
  } as PublicKeyCredentialRequestOptions;

  const cred = (await withTimeout((signal) =>
    navigator.credentials.get({ publicKey, signal }))) as PublicKeyCredential | null;
  if (!cred) throw new Error("no assertion");
  const res = cred.response as AuthenticatorAssertionResponse;

  return {
    id: cred.id,
    type: cred.type,
    response: {
      clientDataJSON: toB64u(res.clientDataJSON),
      authenticatorData: toB64u(res.authenticatorData),
      signature: toB64u(res.signature),
      userHandle: res.userHandle ? toB64u(res.userHandle) : null,
    },
  };
}

export const __testing = { fromB64u, toB64u };
